import React, { useState, useEffect } from 'react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TablaIcon from '../components/TablaIcon';
import t from '../theme';

function StatCard({ label, value, sub, color, link }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => link && navigate(link)}
      style={{ ...t.card, padding:'18px 20px', display:'flex', alignItems:'center', gap:'14px',
        cursor: link ? 'pointer' : 'default', borderLeft:`3px solid ${color}` }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'24px', fontWeight:600, color, lineHeight:1 }}>{value ?? '—'}</div>
        <div style={{ fontSize:'12px', color:t.text3, marginTop:'4px' }}>{label}</div>
        {sub && <div style={{ fontSize:'11px', color:t.text3, marginTop:'2px' }}>{sub}</div>}
      </div>
    </div>
  );
}

function QuickCard({ label, desc, link }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate(link)}
      style={{ ...t.card, padding:'16px 18px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:'14px', fontWeight:500, color:t.text }}>{label}</div>
        <div style={{ fontSize:'12px', color:t.text3, marginTop:'2px' }}>{desc}</div>
      </div>
      <span style={{ color:t.text3, fontSize:'16px' }}>›</span>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ ...t.card, padding:'20px' }}>
      <div style={{ fontSize:'11px', fontWeight:600, color:t.text3, textTransform:'uppercase',
        letterSpacing:'0.6px', marginBottom:'14px' }}>{title}</div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/dashboard')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };
  const name = isAdmin ? (user?.username?.charAt(0).toUpperCase() + user?.username?.slice(1)) : (user?.student?.full_name?.split(' ')[0] || user?.username);
  const fmtDT = dt => new Date(dt).toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })
    + ' · ' + new Date(dt).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' });

  return (
    <div>
      {/* Banner — uses TablaIcon instead of drum emoji */}
      <div style={{ ...t.card, padding: window.innerWidth <= 768 ? '18px' : '28px 32px', marginBottom:'24px',
        display:'flex', alignItems:'center', gap:'20px', flexWrap:'wrap',
        borderLeft:`3px solid ${t.accent}` }}>
        <TablaIcon size={48} color={t.accent}/>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'10px', fontWeight:600, color:t.accent,
            textTransform:'uppercase', letterSpacing:'1px', marginBottom:'6px' }}>
            {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}
          </div>
          <h2 style={{ fontSize:'26px', fontWeight:300, color:t.text, letterSpacing:'-0.5px',
            fontFamily:t.fontSerif, margin:0 }}>
            {greet()}, {name}
          </h2>
          <p style={{ fontSize:'13px', color:t.text3, margin:'6px 0 0' }}>
            {isAdmin ? 'Live overview of RhythMicky Tabla Institute.' : 'Your learning summary for today.'}
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ padding:'48px', textAlign:'center', color:t.text3 }}>Loading...</div>
      ) : isAdmin ? (
        <AdminDashboard data={data} fmtDT={fmtDT} />
      ) : (
        <StudentDashboard data={data} fmtDT={fmtDT} />
      )}
    </div>
  );
}

function AdminDashboard({ data, fmtDT }) {
  const isMobile = useIsMobile();
  const st = data?.stats || {};
  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'12px', marginBottom:'20px' }}>
        <StatCard label="Students"          value={st.totalStudents}   sub={st.newStudentsThisMonth > 0 ? `+${st.newStudentsThisMonth} this month` : null} color={t.blue}   link="/students"/>
        <StatCard label="Pending Applications" value={st.pendingApplications || 0} sub={st.pendingApplications > 0 ? "Awaiting approval" : "All clear"} color={st.pendingApplications > 0 ? t.orange : t.green} link="/approvals"/>
        <StatCard label="Classes"           value={st.totalClasses}    color={t.purple} link="/classes"/>
        <StatCard label="Lessons"           value={st.totalLessons}    color={t.green}  link="/lessons"/>
        <StatCard label="Upcoming Sessions" value={st.upcomingSessions} color={t.accent} link="/schedule"/>
        <StatCard label="Homework Sets"     value={st.totalHomework}   sub={st.overdueHomework > 0 ? `${st.overdueHomework} overdue` : null} color={st.overdueHomework > 0 ? t.red : t.orange} link="/homework"/>
        <StatCard label="Students Tracked"  value={`${st.studentsWithProgress||0}/${st.totalStudents}`} color={t.blue} link="/progress"/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'16px', marginBottom:'20px' }}>
        <Panel title="Next Session">
          {data?.nextSession ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              <div style={{ fontSize:'15px', fontWeight:500, color:t.text }}>{fmtDT(data.nextSession.date_time)}</div>
              {data.nextSession.notes && <div style={{ fontSize:'13px', color:t.text2, background:t.surface2, padding:'8px 12px', borderRadius:t.r, border:`1px solid ${t.border}` }}>{data.nextSession.notes}</div>}
              <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                {data.nextSession.students?.map(s => (
                  <span key={s.id} style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text2, padding:'3px 10px', borderRadius:'20px', fontSize:'12px' }}>{s.full_name}</span>
                ))}
              </div>
            </div>
          ) : <div style={{ color:t.text3, fontSize:'13px' }}>No upcoming sessions.</div>}
        </Panel>

        <Panel title="Recent Assignments">
          {data?.recentHomework?.length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {data.recentHomework.map(hw => (
                <div key={hw.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'10px 12px', background:t.surface2, borderRadius:t.r, border:`1px solid ${t.border}` }}>
                  <span style={{ fontSize:'13px', fontWeight:500, color:t.text }}>{hw.title}</span>
                  <span style={{ fontSize:'11px', color:t.text3 }}>{hw.students?.length || 0} students</span>
                </div>
              ))}
            </div>
          ) : <div style={{ color:t.text3, fontSize:'13px' }}>No assignments yet.</div>}
        </Panel>
      </div>

      <div style={{ fontSize:'11px', fontWeight:600, color:t.text3, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'12px' }}>Quick Access</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'10px' }}>
        <QuickCard label="Students"           desc="Manage enrollments"      link="/students"/>
        <QuickCard label="Classes"            desc="View & edit lessons"     link="/classes"/>
        <QuickCard label="Standalone Lessons" desc="Individual assignments"  link="/lessons"/>
        <QuickCard label="Progress"           desc="Update student scores"   link="/progress"/>
        <QuickCard label="Homework"           desc="Assign & grade ryaaz"    link="/homework"/>
        <QuickCard label="Export Data"        desc="Download reports"        link="/export"/>
      </div>
    </>
  );
}

function StudentDashboard({ data, fmtDT }) {
  const isMobile = useIsMobile();
  const st = data?.stats || {};
  const SC = { assigned: t.blue, in_progress: t.orange, completed: t.green };
  const SL = { assigned:'Assigned', in_progress:'In Progress', completed:'Completed' };

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'12px', marginBottom:'20px' }}>
        <StatCard label="My Classes"      value={st.myClasses}    color={t.blue}   link="/classes"/>
        <StatCard label="Pending Homework" value={st.pendingHomework} color={st.pendingHomework > 0 ? t.orange : t.green} link="/homework"/>
        <StatCard label="Overall Progress" value={st.overallProgress !== null ? `${st.overallProgress}%` : '—'} color={t.purple} link="/progress"/>
        <StatCard label="Next Session"    value={st.nextSession || 'None'} color={t.accent} link="/schedule"/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'16px', marginBottom:'20px' }}>
        <Panel title="My Homework">
          {data?.homework?.length ? data.homework.map(hw => {
            const rec = hw.myRecord;
            const status = rec?.status || 'assigned';
            const color  = SC[status];
            const overdue = hw.due_date && new Date(hw.due_date) < new Date() && status !== 'completed';
            return (
              <div key={hw.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'10px 12px', background:t.surface2, borderRadius:t.r, border:`1px solid ${t.border}`, marginBottom:'6px' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:500, color:t.text }}>{hw.title}</div>
                  {hw.due_date && <div style={{ fontSize:'11px', color:overdue ? t.red : t.text3, marginTop:'2px' }}>
                    Due: {new Date(hw.due_date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}{overdue ? ' · Overdue' : ''}
                  </div>}
                </div>
                <span style={{ background:color+'18', color, padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:600, flexShrink:0 }}>{SL[status]}</span>
              </div>
            );
          }) : <div style={{ color:t.text3, fontSize:'13px' }}>No homework assigned yet.</div>}
        </Panel>

        <Panel title="Next Session">
          {data?.nextSession ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              <div style={{ fontSize:'15px', fontWeight:500, color:t.text }}>{fmtDT(data.nextSession.date_time)}</div>
              {data.nextSession.notes && <div style={{ fontSize:'13px', color:t.text2, background:t.surface2, padding:'8px 12px', borderRadius:t.r, border:`1px solid ${t.border}` }}>{data.nextSession.notes}</div>}
              <span style={{ background:t.greenLight, color:t.green, padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:600, alignSelf:'flex-start' }}>✓ Scheduled</span>
            </div>
          ) : <div style={{ color:t.text3, fontSize:'13px' }}>No upcoming sessions.</div>}
        </Panel>
      </div>

      <div style={{ fontSize:'11px', fontWeight:600, color:t.text3, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'12px' }}>Quick Access</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'10px' }}>
        <QuickCard label="My Classes"  desc="View lessons"         link="/classes"/>
        <QuickCard label="My Lessons"  desc="Individual lessons"   link="/lessons"/>
        <QuickCard label="My Homework" desc="Check assignments"    link="/homework"/>
        <QuickCard label="My Progress" desc="See your scores"      link="/progress"/>
        <QuickCard label="My Media"    desc="View recordings"      link="/media"/>
        <QuickCard label="My Schedule" desc="View sessions"        link="/schedule"/>
      </div>
    </>
  );
}
